/**
 * Tabs.jsx
 * Created by Michael Stanaland on 6/21/18.
 * based on previous Tabs implementation by Destin Frasier on 7/24/17.
 */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Slider from './Slider';

/**
 * Renders a tab list with buttons, followed by the active tab panel.
 *
 * If the tab list does not fit in the wrapping container, the list becomes
 * horizontally scrollable and arrow(s) are added to each end to indicate which
 * direction(s) have more content.
 *
 * Basic usage
 * ===================================================================
 *
 * import Tabs from "./sharedComponents/Tabs";
 * import Panel from "./sharedComponents/Panel";
 *
 * <Tabs>
 *   <Panel label="Tab one">
 *     <div>Panel contents</div>
 *   </Panel>
 *   <Panel label="Tab two">
 *     <div>Panel contents</div>
 *   </Panel>
 * </Tabs>
 */

const propTypes = {
    activeIndex: PropTypes.number,
    children: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.element
    ]).isRequired,
    selectedIndexChanged: PropTypes.func,
    indentTabs: PropTypes.bool,
    className: PropTypes.string
};

const defaultProps = {
    activeIndex: 0,
    indentTabs: false
};

export default class Tabs extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            activeIndex: this.props.activeIndex
        };

        this.renderTabButtons = this.renderTabButtons.bind(this);
        this.renderTabPanels = this.renderTabPanels.bind(this);
        this.handleTabClick = this.handleTabClick.bind(this);
    }

    handleTabClick(e, activeIndex) {
        e.preventDefault();
        this.setState({ activeIndex }, () => {
            // Callback once state has been updated
            if (this.props.selectedIndexChanged) {
                this.props.selectedIndexChanged(activeIndex);
            }
        });
    }

    renderTabButtons() {
        return this.props.children
            .map((child, index) => {
                const tabID = index;
                const selected = tabID === this.state.activeIndex;
                const className = selected ? 'rec-tab rec-tab-active' : 'rec-tab';

                return (
                    <li
                        key={`tab-${tabID}`}
                        className={className}
                        role="presentation">
                        <button
                            role="tab"
                            aria-selected={selected}
                            onClick={(event) => this.handleTabClick(event, index)}>
                            {child.props.label}
                        </button>
                    </li>
                );
            });
    }

    renderTabPanels() {
        return this.props.children
            .map((child, index) => {
                const tabID = index;
                const selected = tabID === this.state.activeIndex;

                let hidden = 'hide';
                if (selected) {
                    hidden = '';
                }

                return (
                    <section
                        {...child.props}
                        key={`tab-panel-${tabID}`}
                        role="tabpanel"
                        aria-hidden={!selected}
                        className={hidden}>
                        {child.props.children}
                    </section>
                );
            });
    }

    render() {
        const { indentTabs, className } = this.props;

        const classes = cx("rec-tab-list", {
            "rec-tab-index": indentTabs,
            [className]: className
        });

        return (
            <div className={classes} data-component="Tabs">
                <ul
                    ref={(listHolder) => {
                        this.listHolder = listHolder;
                    }}
                    className="rec-tabs"
                    role="tablist">
                    <Slider>
                        {this.renderTabButtons()}
                    </Slider>
                </ul>

                {this.renderTabPanels()}
            </div>
        );
    }
}

Tabs.propTypes = propTypes;
Tabs.defaultProps = defaultProps;
